/** Probate 173 — Stream F */
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  probateFieldLabelClass,
  probateInputClass,
  ProbateBackLink,
  ProbateChecklistCard,
  ProbateChecklistProgress,
  ProbateContextBlock,
} from './probateStepUi';
import { useProbateDocuments } from './useProbateDocuments';
import { useProbateStep } from './useProbateStep';

const STEP_ID = 'prob-3';
const FORM_FIELDS =
  'attorneyName,attorneyFirm,attorneyPhone,attorneyEmail,attorneyEngagedDate,cpaName,cpaFirm,cpaPhone,cpaEmail,cpaEngagedDate';

export default function SFrame173() {
  const {
    estateId,
    isArchived,
    error,
    stepDef,
    g,
    checklistDefs,
    checklistCompleted,
    checklistTotal,
    checkedIds,
    toggleItem,
    formValues,
    setField,
    touchActivity,
  } = useProbateStep(STEP_ID, FORM_FIELDS);

  const ro = !!isArchived;
  const { documents, upload, remove } = useProbateDocuments(estateId, STEP_ID, ro);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <ProbateBackLink estateId={estateId} />
      {error ? (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      ) : null}
      <h1 className="text-2xl font-semibold text-stone-800 mb-2">{stepDef?.title}</h1>
      {g?.point && g?.goal ? <ProbateContextBlock point={g.point} goal={g.goal} /> : null}
      <ProbateChecklistProgress completed={checklistCompleted} total={checklistTotal} />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-4 space-y-6">
        <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide">Inputs</h2>
        <fieldset className="space-y-3">
          <legend className={probateFieldLabelClass}>Attorney</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="attorneyName">Attorney name</label>
              <input id="attorneyName" className={probateInputClass} disabled={ro} placeholder="Name"
                value={formValues.attorneyName ?? ''} onChange={(e) => setField('attorneyName', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="attorneyFirm">Attorney firm</label>
              <input id="attorneyFirm" className={probateInputClass} disabled={ro} placeholder="Firm"
                value={formValues.attorneyFirm ?? ''} onChange={(e) => setField('attorneyFirm', e.target.value)} />
            </div>
            <div>
              <label className="sr-only" htmlFor="attorneyPhone">Attorney phone</label>
              <input id="attorneyPhone" className={probateInputClass} disabled={ro} placeholder="Phone"
                value={formValues.attorneyPhone ?? ''} onChange={(e) => setField('attorneyPhone', e.target.value)} />
            </div>
            <div>
              <label className="sr-only" htmlFor="attorneyEmail">Attorney email</label>
              <input id="attorneyEmail" type="email" className={probateInputClass} disabled={ro} placeholder="Email"
                value={formValues.attorneyEmail ?? ''} onChange={(e) => setField('attorneyEmail', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="attorneyEngagedDate">Date engaged</label>
              <input id="attorneyEngagedDate" type="date" className={probateInputClass} disabled={ro}
                value={formValues.attorneyEngagedDate ?? ''} onChange={(e) => setField('attorneyEngagedDate', e.target.value)} />
            </div>
          </div>
        </fieldset>
        <fieldset className="space-y-3">
          <legend className={probateFieldLabelClass}>CPA / tax professional</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="cpaName">CPA name</label>
              <input id="cpaName" className={probateInputClass} disabled={ro} placeholder="Name"
                value={formValues.cpaName ?? ''} onChange={(e) => setField('cpaName', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="cpaFirm">CPA firm</label>
              <input id="cpaFirm" className={probateInputClass} disabled={ro} placeholder="Firm"
                value={formValues.cpaFirm ?? ''} onChange={(e) => setField('cpaFirm', e.target.value)} />
            </div>
            <div>
              <label className="sr-only" htmlFor="cpaPhone">CPA phone</label>
              <input id="cpaPhone" className={probateInputClass} disabled={ro} placeholder="Phone"
                value={formValues.cpaPhone ?? ''} onChange={(e) => setField('cpaPhone', e.target.value)} />
            </div>
            <div>
              <label className="sr-only" htmlFor="cpaEmail">CPA email</label>
              <input id="cpaEmail" type="email" className={probateInputClass} disabled={ro} placeholder="Email"
                value={formValues.cpaEmail ?? ''} onChange={(e) => setField('cpaEmail', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="sr-only" htmlFor="cpaEngagedDate">Date engaged</label>
              <input id="cpaEngagedDate" type="date" className={probateInputClass} disabled={ro}
                value={formValues.cpaEngagedDate ?? ''} onChange={(e) => setField('cpaEngagedDate', e.target.value)} />
            </div>
          </div>
        </fieldset>
      </div>

      <h2 className="text-sm font-semibold text-stone-800 uppercase tracking-wide mb-2">Checklist</h2>
      <ProbateChecklistCard items={checklistDefs} checkedIds={checkedIds} toggleItem={toggleItem} disabled={ro} />

      <div className="bg-white rounded-xl border border-stone-200 p-5">
        <DocumentUploadSection
          documents={documents}
          onUpload={async (f) => { await touchActivity(); await upload(f); }}
          onDelete={ro ? undefined : (id) => remove(id)}
          disabled={ro}
        />
        <p className="text-xs text-stone-500 mt-3">Upload signed engagement letters and related agreements.</p>
      </div>
    </div>
  );
}
